import { ReactElement, useCallback } from 'react'
import { Box, Card, Chip, Skeleton, Typography } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { dataGridSx } from '../../styles'
import {
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../../constants'
import type { UserRow } from '../utils/user-transforms'

type GroupUsersCardProps = {
  users: UserRow[]
  isLoading: boolean
}

let userColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'primaryEmail',
    headerName: 'Email',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'licenseTypes',
    headerName: 'License Types',
    flex: 1,
    minWidth: 150,
    valueFormatter: (value: string[]) => value?.join(', ') || 'None',
  },
]

export default function GroupUsersCard({
  users,
  isLoading,
}: GroupUsersCardProps): ReactElement {
  let GroupUsersToolbar = useCallback(
    () => <DataGridToolbar fileName="group-users" />,
    [],
  )

  return (
    <Card className="p-6 mb-6">
      <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <Typography variant="h6" component="h2">
          Users
        </Typography>

        <Chip
          label={`${users.length} user${users.length !== 1 ? 's' : ''}`}
          size="medium"
          color="primary"
        />
      </Box>

      {isLoading ? (
        <Skeleton
          variant="rectangular"
          height={400}
          sx={{ borderRadius: 1 }}
          aria-label="Loading users"
        />
      ) : (
        <DataGridPremium
          rows={users}
          columns={userColumns}
          slots={{ toolbar: GroupUsersToolbar }}
          showToolbar
          initialState={{
            pagination: {
              paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
            },
          }}
          disableRowSelectionOnClick
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          sx={dataGridSx}
          aria-label="Group users"
        />
      )}
    </Card>
  )
}
